import type {
  ISbDimensions,
  ISbResult,
  ISbStories,
  ISbStoriesParams,
  ISbStoryParams,
} from 'storyblok-js-client'

import type { AsyncDataOptions } from '#app'
import type { ISbStoryData } from 'storyblok'
import type { StoryblokBridgeConfigV2 } from '@storyblok/vue'

export interface DatasourceEntry {
  id: number
  name: string
  value: string
  dimension_value?: string
}

export interface ISbLinksParams
  extends Pick<
    ISbStoriesParams,
    | 'token'
    | 'starts_with'
    | 'version'
    | 'cv'
    // | 'with_parent'
    | 'page'
    | 'per_page'
  > {
  include_dates?: number
  pagination?: number
}

export interface ISbDatasources extends ISbResult {
  data: { datasources: Datasource[] }
}

export interface ISbDatasourceEntries extends ISbResult {
  data: { datasource_entries: DatasourceEntry[] }
}

export interface Datasource {
  id: number
  name: string
  slug: string
  dimensions: ISbDimensions['dimensions']
}

export type FetchDataType =
  | 'stories'
  | 'links'
  | 'datasources'
  | 'datasource_entries'
export type FetchAPIOptions =
  | ISbStoriesParams
  | ISbLinksParams
  | Pick<ISbStoriesParams, 'token' | 'page' | 'per_page' | 'dimension'>

interface IGenericSbStories<T> extends Omit<ISbStories, 'data'> {
  data: Omit<ISbStories['data'], 'stories'> & { stories: ISbStoryData<T>[] }
}

export default function () {
  const route = useRoute()

  const storyblokApiInstance = useStoryblokApi()
  const runtimeConfig = useRuntimeConfig()
  const appConfig = useAppConfig()

  // Simple check to detect if the page is being viewed in the Storyblok editor
  const isStoryblokEditor = computed(() => !!route.query?._storyblok)

  /**
   * Version of Storyblok story to return.
   * Always returns `published` unless `draft` is specified in the runtime config or the page is being viewed in the Storyblok editor.
   * Warning: If you attempt to access the `draft` version using a `public` token, the site will break with a 500 error!
   */
  const storyblokVersion = computed(() =>
    isStoryblokEditor.value || runtimeConfig.public.sb.version === 'draft'
      ? 'draft'
      : 'published',
  )

  /**
   * Remap where the data for a route will be loaded from
   */
  const getStoryPath = function (path: string) {
    return path === '' ? 'index' : path
  }

  /**
   * Async GET request for single story - used on page load.
   * Also establishes the Storyblok bridge functionality.
   */
  const fetchAsyncStory = async (
    url: MaybeRefOrGetter<string>,
    apiOptions: MaybeRefOrGetter<
      Omit<ISbStoryParams, 'resolve_relations' | 'version'>
    > = {},
    bridgeOptions: MaybeRefOrGetter<
      Omit<StoryblokBridgeConfigV2, 'resolveRelations'>
    > = {},
  ) => {
    const amendedApiOptions: ISbStoryParams = {
      ...toValue(apiOptions),
      resolve_relations: appConfig.storyblok.resolveRelations.join(','),
      resolve_links: 'story',
      version: storyblokVersion.value,
    }

    const amendedBridgeOptions: StoryblokBridgeConfigV2 = {
      ...toValue(bridgeOptions),
      resolveLinks: 'story',
      resolveRelations: appConfig.storyblok.resolveRelations.join(','),
    }

    const story = await useAsyncStoryblok(
      getStoryPath(toValue(url)),
      amendedApiOptions,
      amendedBridgeOptions,
    )

    // Note -  we could/should throw an error here if the story is not found (`!story.value`),
    // However as our 404 page also uses this to load the error page story, we need be stuck in an error loop!
    // So need to handle states where the story is not found, in the calling code!

    // Note: This ensures the ListSearchPage (which provides a search bar) is used for the resources and service-providers routes
    if (['resources/', 'service-providers/'].includes(story.value?.full_slug)) {
      story.value.content.component = 'ListSearchPage'
    }

    return story
  }

  /**
   * Async GET request to the Content Delivery API.
   * Caches requests using useState().
   */
  const fetchAsyncData = <T, K = IGenericSbStories<T>>(
    dataType: MaybeRefOrGetter<FetchDataType>,
    apiOptions: MaybeRefOrGetter<FetchAPIOptions> = {},
    options?: AsyncDataOptions<K>,
  ) => {
    const uniqueKey = computed(() => JSON.stringify(toValue(apiOptions)))
    return useAsyncData<K>(
      uniqueKey.value,
      () =>
        storyblokApiInstance
          .get(`cdn/${toValue(dataType)}/`, {
            ...toValue(apiOptions),
            version: storyblokVersion.value,
          })
          .then((response) => response as K),
      { ...options, getCachedData: getCachedDataDefault },
    )
  }

  return {
    storyblokVersion,
    isStoryblokEditor,
    fetchAsyncStory,
    fetchAsyncData,
  }
}
